import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    PlayArrow as SyncIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Cable as ConnectionIcon,
} from '@mui/icons-material';
import { integrationService } from '../../services/integrationService';
import { ExternalSystem, SystemHealth } from '../../types/integration';

const IntegrationManagement: React.FC = () => {
    const [systems, setSystems] = useState<ExternalSystem[]>([]);
    const [healthData, setHealthData] = useState<SystemHealth[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState<number | null>(null);
    const [testing, setTesting] = useState<number | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [systemsData, healthData] = await Promise.all([
                integrationService.getSystems(),
                integrationService.getSystemHealth(),
            ]);
            setSystems(systemsData);
            setHealthData(healthData);
        } catch (err: any) {
            setError(err.message || 'Failed to load integration data');
        } finally {
            setLoading(false);
        }
    };

    const handleTestConnection = async (systemId: number) => {
        try {
            setTesting(systemId);
            const result = await integrationService.testConnection(systemId);
            alert(result.success ? `✓ ${result.message}` : `✗ ${result.message}`);
            await loadData();
        } catch (err: any) {
            alert(`Connection test failed: ${err.message}`);
        } finally {
            setTesting(null);
        }
    };

    const handleTriggerSync = async (systemId: number) => {
        try {
            setSyncing(systemId);
            const result = await integrationService.triggerSync(systemId);
            alert(`Sync triggered: ${result.message}`);
            await loadData();
        } catch (err: any) {
            alert(`Sync failed: ${err.message}`);
        } finally {
            setSyncing(null);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
            case 'success':
            case 'completed':
                return 'success';
            case 'error':
            case 'failed':
                return 'error';
            case 'maintenance':
            case 'in_progress':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case 'success':
            case 'completed':
                return <CheckIcon fontSize="small" />;
            case 'failed':
            case 'error':
                return <ErrorIcon fontSize="small" />;
            default:
                return <WarningIcon fontSize="small" />;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Integration Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* System Health Overview */}
            <Grid container spacing={3} mb={3}>
                {healthData.map((health) => (
                    <Grid item xs={12} md={6} lg={4} key={health.system_id}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">{health.system_name}</Typography>
                                    <Chip
                                        label={health.status}
                                        color={getStatusColor(health.status)}
                                        size="small"
                                    />
                                </Box>

                                <Box mb={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        Type: {health.system_type}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Health: {health.health_status}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Connection: {health.connection_status}
                                    </Typography>
                                </Box>

                                {health.last_sync_date && (
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            Last Sync: {new Date(health.last_sync_date).toLocaleString()}
                                        </Typography>
                                        <Chip
                                            label={health.last_sync_status || 'Unknown'}
                                            color={getStatusColor(health.last_sync_status || '')}
                                            size="small"
                                            icon={getStatusIcon(health.last_sync_status || '')}
                                        />
                                    </Box>
                                )}

                                <Box display="flex" gap={1}>
                                    <Tooltip title="Test Connection">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleTestConnection(health.system_id)}
                                            disabled={testing === health.system_id}
                                        >
                                            {testing === health.system_id ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <ConnectionIcon />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Trigger Sync">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleTriggerSync(health.system_id)}
                                            disabled={syncing === health.system_id || !health.is_enabled}
                                        >
                                            {syncing === health.system_id ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <SyncIcon />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Systems Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        External Systems
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Enabled</TableCell>
                                    <TableCell>Health</TableCell>
                                    <TableCell>Last Check</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {systems.map((system) => (
                                    <TableRow key={system.id}>
                                        <TableCell>{system.name}</TableCell>
                                        <TableCell>{system.system_type}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={system.status}
                                                color={getStatusColor(system.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {system.is_enabled ? (
                                                <CheckIcon color="success" />
                                            ) : (
                                                <ErrorIcon color="disabled" />
                                            )}
                                        </TableCell>
                                        <TableCell>{system.health_status}</TableCell>
                                        <TableCell>
                                            {system.last_health_check
                                                ? new Date(system.last_health_check).toLocaleString()
                                                : 'Never'}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                <Tooltip title="Test Connection">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleTestConnection(system.id)}
                                                        disabled={testing === system.id}
                                                    >
                                                        {testing === system.id ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            <ConnectionIcon />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Trigger Sync">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleTriggerSync(system.id)}
                                                        disabled={syncing === system.id || !system.is_enabled}
                                                    >
                                                        {syncing === system.id ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            <SyncIcon />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default IntegrationManagement;
